import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../services/config';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Search,
  FilterList,
  Edit,
  Delete,
  Visibility,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface CostCode {
  _id: string;
  code: string;
  description: string;
  category: 'labor' | 'material' | 'equipment' | 'subcontractor' | 'overhead';
  unit: string;
  unitCost: number;
  budgetAmount: number;
  actualAmount: number;
  variance: number;
  status: 'active' | 'inactive' | 'archived';
  project?: {
    name: string;
  };
  lastUpdated: string;
  updatedBy: {
    firstName: string;
    lastName: string;
  };
}

const CostCodes: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [costCodes, setCostCodes] = useState<CostCode[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCostCode, setSelectedCostCode] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCostCode, setEditingCostCode] = useState<CostCode | null>(null);

  useEffect(() => {
    const mapCostCode = (cc: any): CostCode => ({
      _id: cc._id || cc.id || '',
      code: cc.code || cc.name || '',
      description: cc.description || cc.name || '',
      category: (cc.category || 'labor') as CostCode['category'],
      unit: cc.unit || cc.unitOfMeasure || 'each',
      unitCost: Number(cc.unitCost ?? cc.standardRate ?? 0),
      budgetAmount: Number(cc.budgetAmount ?? 0),
      actualAmount: Number(cc.actualAmount ?? 0),
      variance: Number(cc.variance ?? (Number(cc.actualAmount ?? 0) - Number(cc.budgetAmount ?? 0))),
      status: (cc.status || 'active') as CostCode['status'],
      project: cc.project ? { name: cc.project?.name || 'Unassigned' } : undefined,
      lastUpdated: cc.updatedAt || cc.lastUpdated || new Date().toISOString(),
      updatedBy: {
        firstName: cc.updatedBy?.firstName || 'System',
        lastName: cc.updatedBy?.lastName || '',
      },
    });

    const fetchCostCodes = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/cost-codes`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });
        if (!res.ok) throw new Error('Failed to fetch cost codes');
        const data = await res.json();
        const items = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        setCostCodes(items.map(mapCostCode));
      } catch (e: any) {
        console.error('Error fetching cost codes:', e);
        setError(e?.message || 'Error fetching cost codes');
        setCostCodes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCostCodes();
  }, []);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, costCodeId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedCostCode(costCodeId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCostCode(null);
  };

  const handleEdit = () => {
    const costCode = costCodes.find(cc => cc._id === selectedCostCode);
    if (costCode) {
      setEditingCostCode(costCode);
      setOpenDialog(true);
    }
    handleMenuClose();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCostCode(null);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'labor':
        return 'primary';
      case 'material':
        return 'secondary';
      case 'equipment':
        return 'warning';
      case 'subcontractor':
        return 'info';
      case 'overhead':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const getVarianceColor = (variance: number) => {
    if (variance > 0) return 'error';
    if (variance < 0) return 'success';
    return 'default';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const filteredCostCodes = costCodes.filter(
    (costCode) => {
      const matchesSearch = 
        costCode.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        costCode.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'all' || costCode.category === filterCategory;
      return matchesSearch && matchesCategory;
    }
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          Cost Codes
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ borderRadius: 2 }}
          onClick={() => navigate('/cost-codes/create')}
        >
          New Cost Code
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              placeholder="Search cost codes..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                label="Category"
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <SelectMenuItem value="all">All</SelectMenuItem>
                <SelectMenuItem value="labor">Labor</SelectMenuItem>
                <SelectMenuItem value="material">Material</SelectMenuItem>
                <SelectMenuItem value="equipment">Equipment</SelectMenuItem>
                <SelectMenuItem value="subcontractor">Subcontractor</SelectMenuItem>
                <SelectMenuItem value="overhead">Overhead</SelectMenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              sx={{ borderRadius: 2 }}
            >
              Filter
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Unit Cost</TableCell>
                <TableCell>Budget</TableCell>
                <TableCell>Actual</TableCell>
                <TableCell>Variance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Project</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCostCodes.map((costCode) => (
                <TableRow 
                  key={costCode._id} 
                  hover
                  onClick={() => navigate(`/cost-codes/${costCode._id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {costCode.code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {costCode.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      per {costCode.unit}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={costCode.category.charAt(0).toUpperCase() + costCode.category.slice(1)}
                      color={getCategoryColor(costCode.category) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(costCode.unitCost)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatCurrency(costCode.budgetAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatCurrency(costCode.actualAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatCurrency(costCode.variance)}
                      color={getVarianceColor(costCode.variance) as any}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={costCode.status.charAt(0).toUpperCase() + costCode.status.slice(1)}
                      color={getStatusColor(costCode.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {costCode.project?.name || 'General'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuClick(e, costCode._id);
                      }}
                      size="small"
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => { handleMenuClose(); }}>
          <Visibility sx={{ mr: 1 }} /> View Details
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <Edit sx={{ mr: 1 }} /> Edit Cost Code
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Delete sx={{ mr: 1 }} /> Archive
        </MenuItem>
      </Menu>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCostCode ? 'Edit Cost Code' : 'New Cost Code'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Cost Code"
              variant="outlined"
              fullWidth
              defaultValue={editingCostCode?.code || ''}
            />
            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              defaultValue={editingCostCode?.description || ''}
            />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                defaultValue={editingCostCode?.category || 'labor'}
                label="Category"
              >
                <SelectMenuItem value="labor">Labor</SelectMenuItem>
                <SelectMenuItem value="material">Material</SelectMenuItem>
                <SelectMenuItem value="equipment">Equipment</SelectMenuItem>
                <SelectMenuItem value="subcontractor">Subcontractor</SelectMenuItem>
                <SelectMenuItem value="overhead">Overhead</SelectMenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Unit"
              variant="outlined"
              fullWidth
              defaultValue={editingCostCode?.unit || ''}
            />
            <TextField
              label="Unit Cost"
              variant="outlined"
              fullWidth
              type="number"
              defaultValue={editingCostCode?.unitCost || ''}
            />
            <TextField
              label="Budget Amount"
              variant="outlined"
              fullWidth
              type="number"
              defaultValue={editingCostCode?.budgetAmount || ''}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleCloseDialog}>
            {editingCostCode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CostCodes;